import React from "react";
import {Box, Typography} from "@mui/material";
import type {BudgetLine, Opportunity} from "../opportunityIntelligenceDemoData";
import {PRESSURE_HEADLINE, PRESSURE_INTRO} from "../opportunityIntelligenceDemoData";
import {formatCompactCurrency, getPressureSourced} from "../opportunityUtils";
import {PlainOpportunityTable} from "./PlainOpportunityTable";

type Props = {
    rows: Opportunity[];
    budgetLines: BudgetLine[];
};

const BudgetLineCard: React.FC<{ line: BudgetLine }> = ({line}) => {
    const needed = formatCompactCurrency(line.savingsNeeded, line.currency);
    return (
        <Box className="oi-budget-card">
            <span className="oi-budget-gl">{line.glCode} · {line.costCenter}</span>
            <Typography className="oi-budget-name">{line.name}</Typography>
            <Typography className="oi-budget-owner">{line.owner} · {line.scope}</Typography>

            <Typography className="oi-budget-value">{needed}</Typography>
            <Typography className="oi-budget-value-label">SAVINGS STILL NEEDED</Typography>

            <div className="oi-budget-bar">
                <div className="oi-budget-bar-fill" style={{width: `${Math.round(line.progress * 100)}%`}}/>
                <span className="oi-budget-bar-thumb" style={{left: `calc(${Math.round(line.progress * 100)}% - 7px)`}}/>
            </div>

            <Typography className="oi-budget-quote">
                “You need to find {needed} in savings on {line.name}. Here are the opportunities we found.”
            </Typography>
        </Box>
    );
};

export const ByPressureView: React.FC<Props> = ({rows, budgetLines}) => {
    const pressureSourced = React.useMemo(() => getPressureSourced(rows), [rows]);

    return (
        <Box>
            <Box className="oi-pressure-callout">
                <Typography className="oi-pressure-title">{PRESSURE_HEADLINE}</Typography>
                <Typography className="oi-pressure-intro">{PRESSURE_INTRO}</Typography>
                <Box className="oi-budget-grid">
                    {budgetLines.map(line => <BudgetLineCard key={line.id} line={line}/>)}
                </Box>
            </Box>

            <Box className="oi-table-card" mt={3}>
                <Box className="oi-table-card-header">
                    <Typography variant="h2" sx={{fontSize: 18, fontWeight: 700}}>
                        Pressure-sourced opportunities ({pressureSourced.length})
                    </Typography>
                    <span className="oi-table-card-hint">
                        Items in the pipeline that originated from a pressured budget line.
                    </span>
                </Box>
                <Box sx={{px: 1, pb: 1}}>
                    <PlainOpportunityTable rows={pressureSourced} showSource/>
                </Box>
            </Box>
        </Box>
    );
};
